import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {
  Box,
  FormControlLabel,
  IconButton,
  MenuItem,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useMemo } from 'react';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import {
  FormikSelect,
  FormikSwitch,
  FormikTextField,
  MaterialAutocomplete,
} from '../../../common/formik-material-ui';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import { isRerouteAction } from '../modals/CadenceModal';

import { useRecoilValueLoadable } from 'recoil';
//import { useParams } from 'react-router-dom';

import clsx from 'clsx';
import { user } from '../../../recoil/atoms/user';
import Headers from '../Headers/Headers';
import InfoPanel from '../InfoPanel/InfoPanel';
import './App.css';

const useStyles = makeStyles((theme) => ({
  icon: {
    height: 'max-content',
    alignSelf: 'center',
  },
  closeIcon: {
    position: 'absolute',
    top: '10px',
    right: '20px',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    width: '18vw',
  },
  smsField: {
    minHeight: '3em',
  },
  spacing: {
    marginLeft: theme.spacing(1),
    marginTop: '13.5px',
  },
  fieldInsideRow: {
    marginTop: '19.8px', // space between fields
    marginBottom: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '110px',
  },
  fieldInsideRowLarge: {
    width: 'auto',
    minWidth: 110,
  },
  webhookUrlInput: {
    '@media (min-width: 1450px)': {
      marginLeft: '33px',
    },
  },
  timeframes: {
    marginTop: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    // width: '65px',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
    height: 'max-content',
    alignSelf: 'center',
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  errorMessage: {
    color: 'red',
    paddingBottom: theme.spacing(1),
  },
}));
const actionTypes = ['sms', 'call', 'reroute', 'webhook'];
const retryAttempts = [
  {
    code: 1,
    name: '1 Time',
  },
  {
    code: 2,
    name: '2 Times',
  },
  {
    code: 3,
    name: '3 Times',
  },
  {
    code: 4,
    name: '4 Times',
  },
];

export function CadenceActions({ formik, smsTemplateOptions, id, hasWebhook }) {
  const classes = useStyles();
  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents;
  const powerlistReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_BUSINESS_POWERLISTS}`,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'v1/getBusinessPowerlists',
      }),
    })
  );

  const powerlists = useMemo(
    () =>
      powerlistReportLoadable?.contents?.response?.data?.powerlists ??
      powerlists,
    [powerlistReportLoadable]
  );
  const cadenceReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_CADENCE_REPORT}?filter=businessId:${userAtomContents?.businessid}`,
    })
  );
  const cadences = useMemo(
    () =>
      cadenceReportLoadable.state === 'hasValue'
        ? [
            ...(cadenceReportLoadable.contents?.response?.data ?? []),
          ]?.sort((a, b) =>
            a?.cadenceName
              ?.toLowerCase()
              ?.localeCompare(b?.cadenceName?.toLowerCase())
          ) ?? []
        : [],
    [cadenceReportLoadable]
  );

  const hasRerouteActionBefore = (index) => {
    return (
      formik.values.cadenceActions.filter(
        (cadenceAction, i) =>
          cadenceAction.status === 'active' &&
          i < index &&
          isRerouteAction(cadenceAction)
      ).length >= 1
    );
  };

  // const classes = useStyles();
  return (
    <div className="container">
      <InfoPanel>
        <div>
          Set your Cadence Actions on this tab. Add Call and SMS actions, use
          SMS templates, Reroute to another Cadence or a PowerList, and
          configure time durations for Actions. Times set for Actions will occur
          after the previous Action is triggered, and your first Action occurs
          after your Webhook trigger. Click the + icon to add another action,
          click the trash icon to remove.
        </div>
      </InfoPanel>
      <div className="row">
        {formik.values.cadenceActions.map((cadenceAction, index) => {
          return (
            cadenceAction.status !== 'deleted' && (
              <>
                <Box key={index} display="flex" width="100%">
                  <div className="col-11">
                    <LabelledOutline key={index} label={`Action ${index + 1}`}>
                      {!hasWebhook && cadenceAction.type === 'webhook' && (
                        <div
                          style={{
                            position: 'relative',
                            top: '10px',
                            right: '3px',
                          }}
                        >
                          <InfoPanel color={'error'}>
                            <div>
                              Webhook action steps will not run while webhooks
                              are disabled for your business. Go to Manage{' '}
                              {'> '}
                              Account Settings {'>'} Options to view/edit this
                              setting.
                            </div>
                          </InfoPanel>
                        </div>
                      )}
                      <div className="row">
                        <FormikSelect
                          name={`cadenceActions.${index}.type`}
                          className={classes.fieldInsideRow}
                          formik={formik}
                          label="Type"
                          size="small"
                          variant="outlined"
                        >
                          {actionTypes
                            // filter out reroute for first row
                            .filter((type) => type !== 'reroute' || index !== 0)
                            .filter((type) => {
                              if (hasWebhook === 0) {
                                if (
                                  formik.values.cadenceActions[index].type ===
                                  'webhook'
                                ) {
                                  return true;
                                } else if (type === 'webhook') {
                                  return false;
                                }
                              }
                              return true;
                            })
                            .map((action, index2) => (
                              <MenuItem key={index2} value={action}>
                                {action === 'call'
                                  ? 'Call'
                                  : action === 'sms'
                                  ? 'SMS'
                                  : action === 'webhook'
                                  ? 'Webhook'
                                  : 'Reroute'}
                              </MenuItem>
                            ))}
                        </FormikSelect>
                        <span className={clsx(classes.spacing, 'timeframe')}>
                          <LabelledOutline
                            label="After"
                            customPadding="5px 8px"
                          >
                            <FormikTextField
                              formik={formik}
                              className={classes.timeframes}
                              name={`cadenceActions.${index}.days`}
                              label="Days"
                              size="small"
                              InputProps={{
                                inputProps: {
                                  min: 0,
                                  max: 90,
                                },
                              }}
                              type="number"
                              variant="outlined"
                            />
                            <FormikTextField
                              className={classes.timeframes}
                              formik={formik}
                              name={`cadenceActions.${index}.hours`}
                              label="Hours"
                              InputProps={{
                                inputProps: {
                                  min: 0,
                                  max: 23,
                                },
                              }}
                              size="small"
                              type="number"
                              variant="outlined"
                            />
                            <FormikTextField
                              className={classes.timeframes}
                              formik={formik}
                              name={`cadenceActions.${index}.minutes`}
                              label="Minutes"
                              InputProps={{
                                inputProps: {
                                  min: 0,
                                  max: 59,
                                },
                              }}
                              size="small"
                              type="number"
                              variant="outlined"
                            />
                          </LabelledOutline>
                        </span>
                        {cadenceAction.type === 'call' && (
                          <FormikSelect
                            name={`cadenceActions.${index}.retryCallEnabled`}
                            className={classes.fieldInsideRow}
                            formik={formik}
                            label="Retry"
                            size="small"
                            variant="outlined"
                          >
                            <MenuItem value={1}>Yes</MenuItem>
                            <MenuItem value={0}>No</MenuItem>
                          </FormikSelect>
                        )}
                        {cadenceAction.type === 'webhook' && (
                          <FormikTextField
                            formik={formik}
                            className={clsx(
                              classes.fieldInsideRow,
                              classes.webhookUrlInput
                            )}
                            style={{
                              width: '349px',
                            }}
                            name={`cadenceActions.${index}.webhookUrl`}
                            required
                            label="Webhook URL"
                            size="small"
                            type="text"
                            variant="outlined"
                          ></FormikTextField>
                        )}
                        {cadenceAction.retryCallEnabled === 1 &&
                          cadenceAction.type === 'call' && (
                            <>
                              <FormikSelect
                                name={`cadenceActions.${index}.maxRetries`}
                                className={classes.fieldInsideRow}
                                formik={formik}
                                label="Attempts"
                                size="small"
                                variant="outlined"
                              >
                                {retryAttempts.map((attempt, index2) => (
                                  <MenuItem key={index2} value={attempt.code}>
                                    {attempt.name}
                                  </MenuItem>
                                ))}
                              </FormikSelect>
                              <span
                                className={clsx(classes.spacing, 'timeframe')}
                              >
                                <LabelledOutline label="Min Duration Between Attempts">
                                  <FormikTextField
                                    formik={formik}
                                    className={classes.timeframes}
                                    name={`cadenceActions.${index}.durationDays`}
                                    label="Days"
                                    size="small"
                                    InputProps={{
                                      inputProps: {
                                        min: 0,
                                        max: 90,
                                      },
                                    }}
                                    type="number"
                                    variant="outlined"
                                  />
                                  <FormikTextField
                                    className={classes.timeframes}
                                    formik={formik}
                                    name={`cadenceActions.${index}.durationHours`}
                                    label="Hours"
                                    InputProps={{
                                      inputProps: {
                                        min: 0,
                                        max: 23,
                                      },
                                    }}
                                    size="small"
                                    type="number"
                                    variant="outlined"
                                  />
                                  <FormikTextField
                                    className={classes.timeframes}
                                    formik={formik}
                                    name={`cadenceActions.${index}.durationMinutes`}
                                    label="Minutes"
                                    InputProps={{
                                      inputProps: {
                                        min: 0,
                                        max: 59,
                                      },
                                    }}
                                    size="small"
                                    type="number"
                                    variant="outlined"
                                  />
                                </LabelledOutline>
                              </span>
                            </>
                          )}
                      </div>
                      {cadenceAction.type === 'sms' && (
                        <div className="row">
                          <div className="col-sm-4">
                            <MaterialAutocomplete
                              type="text"
                              fullWidth
                              label="SMS Template Name"
                              size="small"
                              variant="outlined"
                              multiple={false}
                              options={
                                smsTemplateOptions?.map((sms) =>
                                  String(sms.smsTemplateid)
                                ) ?? []
                              }
                              value={String(cadenceAction.smsTemplateId)}
                              disabled={cadenceAction.custom}
                              onChange={(event, newValue) =>
                                formik.setFieldValue(
                                  `cadenceActions.${index}.smsTemplateId`,
                                  String(newValue)
                                )
                              }
                              getOptionLabel={(option) => {
                                return (
                                  smsTemplateOptions?.find(
                                    (sms) =>
                                      String(sms.smsTemplateid) ===
                                      String(option)
                                  )?.smsTemplateName ?? ''
                                );
                              }} // collapse to empty string if no option found
                              isOptionEqualToValue={(option, value) => {
                                return option === value;
                              }}
                            />
                            {cadenceAction.smsTemplateId && (
                              <FormControlLabel
                                style={{ marginTop: '5px' }}
                                control={
                                  <FormikSwitch
                                    name={`cadenceActions.${index}.custom`}
                                    formik={formik}
                                    color="primary"
                                    type="checkbox"
                                  />
                                }
                                label="Customize Message"
                              />
                            )}
                          </div>
                          <div className="col-sm-8">
                            <FormikTextField
                              multiline={true}
                              formik={formik}
                              name={`cadenceActions.${index}.smsMessage`}
                              label="SMS Template Message"
                              size="small"
                              variant="outlined"
                              required
                              fullWidth
                              // className={classes.smsWidth}
                              InputProps={{
                                readOnly:
                                  !!cadenceAction.smsTemplateId &&
                                  !cadenceAction.custom,
                                classes: {
                                  input: classes.smsField,
                                },
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {cadenceAction.type === 'webhook' && (
                        <div className="row" style={{ marginLeft: '-5px' }}>
                          <div
                            className="col-sm-12"
                            style={{
                              display: 'contents',
                            }}
                          >
                            <Headers
                              cadenceStyles={true}
                              formik={formik}
                              formikHeadersPath={`cadenceActions.${index}.webhookHeaders`}
                            />
                          </div>
                        </div>
                      )}
                      {cadenceAction.type === 'reroute' && (
                        <div className="row">
                          <FormikSelect
                            name={`cadenceActions.${index}.rerouteType`}
                            className={classes.fieldInsideRow}
                            formik={formik}
                            label="Action"
                            size="small"
                            variant="outlined"
                          >
                            {/* if the disposition is the same as one that has been already selected then filter the array of options based off what has been selected otherwise display both options  */}
                            {/* if our current disposition is the same as one that has already been selected then  */}
                            <MenuItem value={'rerouteToCadence'}>
                              Reroute To Cadence
                            </MenuItem>
                            <MenuItem value={'rerouteToPowerlist'}>
                              Reroute To PowerList
                            </MenuItem>
                          </FormikSelect>
                          {formik.values.cadenceActions[index].rerouteType ===
                            'rerouteToCadence' && (
                            <FormikSelect
                              name={`cadenceActions.${index}.rerouteToCadenceId`}
                              className={clsx(
                                classes.fieldInsideRow,
                                classes.fieldInsideRowLarge
                              )}
                              formik={formik}
                              label="Cadence"
                              size="small"
                              variant="outlined"
                            >
                              {cadences
                                .filter((cadence) => cadence.cadenceId !== +id)
                                .map((cadence, index) => (
                                  <MenuItem
                                    key={index}
                                    value={cadence.cadenceId}
                                  >
                                    {cadence.cadenceName}
                                  </MenuItem>
                                ))}
                            </FormikSelect>
                          )}
                          {formik.values.cadenceActions[index].rerouteType ===
                            'rerouteToPowerlist' && (
                            <FormikSelect
                              name={`cadenceActions.${index}.rerouteToPowerlistId`}
                              className={clsx(
                                classes.fieldInsideRow,
                                classes.fieldInsideRowLarge
                              )}
                              formik={formik}
                              label="PowerList"
                              size="small"
                              variant="outlined"
                            >
                              {powerlists
                                .filter(
                                  (powerlist) =>
                                    powerlist.isAgentPowerlist !== 1
                                )
                                .map((powerlist, index) => (
                                  <MenuItem
                                    key={index}
                                    value={powerlist.powerlistId}
                                  >
                                    {powerlist.name}
                                  </MenuItem>
                                ))}
                            </FormikSelect>
                          )}
                        </div>
                      )}
                    </LabelledOutline>
                  </div>
                  <div
                    className="col-1"
                    style={{
                      marginLeft: 'auto',
                      display: 'flex',
                      flexWrap: 'nowrap',
                    }}
                  >
                    <Tooltip title="add">
                      <IconButton
                        className={classes.icon}
                        aria-label="add"
                        color="primary"
                        onClick={() => {
                          formik.setFieldValue('cadenceActions', [
                            ...formik.values.cadenceActions.slice(0, index + 1),
                            {
                              type: 'call',
                              days: 0,
                              hours: 0,
                              minutes: 0,
                              durationDays: 0,
                              durationHours: 0,
                              durationMinutes: 0,
                              retryCallEnabled: 0,
                              maxRetries: '1',
                              smsTemplateId: '',
                              smsMessage: '',
                              custom: false,
                              status: 'active',
                              webhookUrl: '',
                              // webhookHeaders: [{ name: '', value: '' }],
                            },
                            ...formik.values.cadenceActions.slice(index + 1),
                          ]);
                        }}
                        disabled={isRerouteAction(cadenceAction)}
                        size="large"
                      >
                        <PlaylistAddIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip key={index} title="remove">
                      <IconButton
                        className={classes.deleteIconButton}
                        aria-label="cancel"
                        onClick={() => {
                          const cadenceActions =
                            cadenceAction.cadenceActionId !== undefined
                              ? [
                                  ...formik.values.cadenceActions.slice(
                                    0,
                                    index
                                  ),
                                  {
                                    ...cadenceAction,
                                    status: 'deleted',
                                  },
                                  ...formik.values.cadenceActions.slice(
                                    index + 1
                                  ),
                                ]
                              : [
                                  ...formik.values.cadenceActions.slice(
                                    0,
                                    index
                                  ),
                                  ...formik.values.cadenceActions.slice(
                                    index + 1
                                  ),
                                ];
                          formik.setFieldValue(
                            'cadenceActions',
                            cadenceActions
                          );
                        }}
                        disabled={formik.values.cadenceActions.length <= 1}
                        size="large"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                </Box>
                {(cadenceAction.type === 'call' ||
                  cadenceAction.type === 'sms') &&
                  hasRerouteActionBefore(index) && (
                    <div display="flex" width="100%" className="col-11">
                      <div className={classes.errorMessage}>
                        Call and SMS Actions cannot be added after a Reroute
                        Action. Please delete this Action or remove/change the
                        Reroute Action type.
                      </div>
                    </div>
                  )}
                {isRerouteAction(cadenceAction) &&
                  hasRerouteActionBefore(index) && (
                    <div display="flex" width="100%" className="col-11">
                      <div className={classes.errorMessage}>
                        Only one Reroute Action allowed per Cadence. Please
                        remove/change this Reroute Action type.
                      </div>
                    </div>
                  )}
              </>
            )
          );
        })}
      </div>
    </div>
  );
}
